import Vue from "vue";
import axios from "../plugins/axios";

/**
 * Coverage Comparison Taxonomy admin store (QUOTE COMP: 07 / S2CWB-232).
 *
 * Every route here is admin-guarded server side and returns 404 while
 * COVERAGE_COMPARISON_ENABLED is off. Responses are camelCase and are stored
 * exactly as the API returns them — there is no local taxonomy and no
 * client-side defaulting of missing fields.
 *
 * Errors are re-thrown so the calling component can surface the API's own
 * message. A failed request never blanks the list it was refreshing.
 */

const ROWS_URL = "/admin/comparison-rows";
const SECTIONS_URL = "/admin/comparison-sections";
const TAXONOMY_URL = "/admin/comparison-taxonomy";
const REANALYZE_URL = "/admin/coverage-analysis/reanalyze";

/** Message an API error carries, or null — never a fabricated default. */
export function apiErrorMessage(error) {
  const message = error?.response?.data?.message;
  return typeof message === "string" && message.trim() ? message : null;
}

const state = () => ({
  sections: [],
  rows: [],
  loading: false,
  saving: false,
  reordering: false,
  error: null,
  filters: {
    sectionCode: null,
    search: "",
    includeInactive: false,
  },
  panel: {
    open: false,
    mode: null, // "create" | "edit"
    row: null,
  },
  // Canonical enums from GET /admin/comparison-taxonomy. Stays null on an older
  // backend that does not send them, which is what the editor keys its
  // inferred-vocabulary fallback off — an empty array would be indistinguishable
  // from "the backend says there are none" and would render an empty select.
  vocabularies: null,
  reanalyzePreview: null,
  // Monotonic token for taxonomy reads. Typing fires overlapping requests and
  // they can settle out of order, so only the newest one is allowed to write.
  latestFetchId: 0,
});

const mutations = {
  SET_TAXONOMY(state, { sections, rows, vocabularies }) {
    state.sections = Array.isArray(sections) ? sections : [];
    state.rows = Array.isArray(rows) ? rows : [];
    state.vocabularies = vocabularies
      ? {
          valueTypes: Array.isArray(vocabularies.valueTypes) ? vocabularies.valueTypes : [],
          sourceScopes: Array.isArray(vocabularies.sourceScopes) ? vocabularies.sourceScopes : [],
        }
      : null;
  },

  SET_ROWS(state, rows) {
    state.rows = Array.isArray(rows) ? rows : [];
  },

  SET_LOADING(state, value) {
    state.loading = Boolean(value);
  },

  SET_SAVING(state, value) {
    state.saving = Boolean(value);
  },

  SET_REORDERING(state, value) {
    state.reordering = Boolean(value);
  },

  SET_ERROR(state, message) {
    state.error = message || null;
  },

  SET_FILTERS(state, filters) {
    Object.keys(filters || {}).forEach((key) => {
      Vue.set(state.filters, key, filters[key]);
    });
  },

  UPSERT_ROW(state, row) {
    if (!row || !row.entityId) return;
    const index = state.rows.findIndex((r) => r.entityId === row.entityId);
    if (index === -1) {
      state.rows.push(row);
    } else {
      Vue.set(state.rows, index, row);
    }
  },

  UPSERT_SECTION(state, section) {
    if (!section || !section.entityId) return;
    const index = state.sections.findIndex((s) => s.entityId === section.entityId);
    if (index === -1) {
      state.sections.push(section);
    } else {
      Vue.set(state.sections, index, section);
    }
  },

  MERGE_ROWS(state, rows) {
    (rows || []).forEach((row) => {
      if (!row || !row.entityId) return;
      const index = state.rows.findIndex((r) => r.entityId === row.entityId);
      if (index === -1) {
        state.rows.push(row);
      } else {
        Vue.set(state.rows, index, row);
      }
    });
  },

  OPEN_PANEL(state, { mode, row }) {
    Vue.set(state, "panel", { open: true, mode, row: row || null });
  },

  CLOSE_PANEL(state) {
    Vue.set(state, "panel", { open: false, mode: null, row: null });
  },

  SET_REANALYZE_PREVIEW(state, preview) {
    state.reanalyzePreview = preview || null;
  },

  START_FETCH(state) {
    state.latestFetchId += 1;
  },
};

const actions = {
  async fetchTaxonomy({ commit, state }, overrides) {
    const filters = { ...state.filters, ...(overrides || {}) };
    const params = {};
    if (filters.sectionCode) params.sectionCode = filters.sectionCode;
    if (filters.search) params.search = filters.search;
    if (filters.includeInactive) params.includeInactive = true;

    commit("START_FETCH");
    const fetchId = state.latestFetchId;
    const isStale = () => state.latestFetchId !== fetchId;

    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      const { data } = await axios.get(TAXONOMY_URL, { params });
      // A slower earlier request must not overwrite a newer one's result.
      if (isStale()) return data;
      commit("SET_TAXONOMY", {
        sections: data?.sections,
        rows: data?.rows,
        vocabularies: data?.vocabularies,
      });
      return data;
    } catch (error) {
      if (!isStale()) commit("SET_ERROR", apiErrorMessage(error));
      throw error;
    } finally {
      // Only the newest request owns the spinner; an overtaken one leaves it on
      // for the request that is still in flight.
      if (!isStale()) commit("SET_LOADING", false);
    }
  },

  async createSection({ commit }, payload) {
    commit("SET_SAVING", true);
    try {
      const { data } = await axios.post(SECTIONS_URL, payload);
      commit("UPSERT_SECTION", data?.section);
      return data?.section;
    } finally {
      commit("SET_SAVING", false);
    }
  },

  async updateSection({ commit }, { entityId, ...payload }) {
    commit("SET_SAVING", true);
    try {
      const { data } = await axios.put(`${SECTIONS_URL}/${entityId}`, payload);
      commit("UPSERT_SECTION", data?.section);
      return data?.section;
    } finally {
      commit("SET_SAVING", false);
    }
  },

  async setSectionActive({ commit }, { entityId, active }) {
    const suffix = active ? "activate" : "deactivate";
    const { data } = await axios.post(`${SECTIONS_URL}/${entityId}/${suffix}`);
    commit("UPSERT_SECTION", data?.section);
    return data?.section;
  },

  async createRow({ commit }, payload) {
    commit("SET_SAVING", true);
    try {
      const { data } = await axios.post(ROWS_URL, payload);
      commit("UPSERT_ROW", data?.row);
      return data?.row;
    } finally {
      commit("SET_SAVING", false);
    }
  },

  /** rowKey is immutable server side; it is never sent on an edit. */
  async updateRow({ commit }, { entityId, ...payload }) {
    // eslint-disable-next-line no-unused-vars
    const { rowKey, ...editable } = payload;
    commit("SET_SAVING", true);
    try {
      const { data } = await axios.put(`${ROWS_URL}/${entityId}`, editable);
      commit("UPSERT_ROW", data?.row);
      return data?.row;
    } finally {
      commit("SET_SAVING", false);
    }
  },

  /**
   * S2CWB-233: the rows the broker's clause deep dive shows beside this one.
   * Its own endpoint because it is display metadata: unlike updateRow it does
   * not record a new taxonomy_version, so no extracted cell goes stale.
   */
  async setRelatedRows({ commit }, { entityId, relatedRowKeys }) {
    commit("SET_SAVING", true);
    try {
      const { data } = await axios.put(`${ROWS_URL}/${entityId}/related-rows`, { relatedRowKeys });
      commit("UPSERT_ROW", data?.row);
      return data?.row;
    } finally {
      commit("SET_SAVING", false);
    }
  },
  async setRowActive({ commit }, { entityId, active }) {
    const suffix = active ? "activate" : "deactivate";
    const { data } = await axios.post(`${ROWS_URL}/${entityId}/${suffix}`);
    commit("UPSERT_ROW", data?.row);
    return data?.row;
  },

  /** orderedRowIds must be EVERY row of the section, in the new order. */
  async reorderRows({ commit }, { sectionId, orderedRowIds }) {
    commit("SET_REORDERING", true);
    try {
      const { data } = await axios.put(`${ROWS_URL}/reorder`, { sectionId, orderedRowIds });
      commit("MERGE_ROWS", data?.rows);
      return data?.rows;
    } finally {
      commit("SET_REORDERING", false);
    }
  },

  /** Step 1 of re-analysis: how much work would this be? */
  async previewReanalysis({ commit }, { organizationId = null, rowKeys = null } = {}) {
    const payload = { dryRun: true, organizationId };
    if (rowKeys && rowKeys.length) payload.rowKeys = rowKeys;
    const { data } = await axios.post(REANALYZE_URL, payload);
    commit("SET_REANALYZE_PREVIEW", {
      affectedQuoteCount: data?.affectedQuoteCount,
      affectedOrganizationCount: data?.affectedOrganizationCount,
    });
    return data;
  },

  /** Step 2: enqueue, only after the admin has seen the preview. */
  async runReanalysis(_context, { organizationId = null, rowKeys = null, force = false } = {}) {
    const payload = { dryRun: false, organizationId, force };
    if (rowKeys && rowKeys.length) payload.rowKeys = rowKeys;
    const { data } = await axios.post(REANALYZE_URL, payload);
    return data;
  },

  setFilters({ commit }, filters) {
    commit("SET_FILTERS", filters);
  },

  openPanel({ commit }, { mode, row }) {
    commit("OPEN_PANEL", { mode, row });
  },

  closePanel({ commit }) {
    commit("CLOSE_PANEL");
  },

  clearReanalyzePreview({ commit }) {
    commit("SET_REANALYZE_PREVIEW", null);
  },
};

const getters = {
  orderedSections: (state) =>
    [...state.sections].sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || String(a.code).localeCompare(String(b.code))
    ),

  sectionByEntityId: (state) => (entityId) => state.sections.find((s) => s.entityId === entityId) || null,

  rowsBySectionId: (state) => (sectionId) =>
    state.rows
      .filter((r) => r.sectionId === sectionId)
      .sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || String(a.rowKey).localeCompare(String(b.rowKey))
      ),

  /** Section order first, then row order — the same order the matrix reads. */
  visibleRows: (state, getters) => {
    const sectionOrder = {};
    getters.orderedSections.forEach((section, index) => {
      sectionOrder[section.entityId] = index;
    });
    return [...state.rows].sort((a, b) => {
      const aSection = sectionOrder[a.sectionId] ?? Number.MAX_SAFE_INTEGER;
      const bSection = sectionOrder[b.sectionId] ?? Number.MAX_SAFE_INTEGER;
      if (aSection !== bSection) return aSection - bSection;
      return (a.displayOrder || 0) - (b.displayOrder || 0) || String(a.rowKey).localeCompare(String(b.rowKey));
    });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
